import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/axios'; 
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

import paymentQR from '../uploads/Payment.jpeg'; 
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

import { 
  Wallet as WalletIcon, 
  IndianRupee, 
  Loader2, 
  History, 
  Zap, 
  ReceiptText,
  ChevronLeft,
  ChevronRight,
  User
} from 'lucide-react';

export default function Wallet() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const isAdmin = user?.role === 'admin';
  const [balance, setBalance] = useState(user?.wallet_balance || 0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const currentPageParam = parseInt(searchParams.get('page'), 10);
  const currentPage = isNaN(currentPageParam) || currentPageParam < 1 ? 1 : currentPageParam;
  
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const [rechargeAmount, setRechargeAmount] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const handlePageChange = (newPage) => {
    setSearchParams((prevParams) => {
      prevParams.set('page', newPage);
      return prevParams;
    });
  };

  const fetchWalletData = useCallback(async () => {
    try {
      setLoading(true);
      const [meRes, txRes] = await Promise.all([
        api.get('/auth/me'),
        api.get(`/billing/transactions?page=${currentPage}&limit=${limit}`)
      ]);
      
      if (meRes.data.success) {
        setBalance(meRes.data.user.wallet_balance || 0);
      }
      
      if (txRes.data.success) {
        setTransactions(txRes.data.transactions || []);
        setTotalPages(txRes.data.meta?.totalPages || 1);
        setTotalRecords(txRes.data.meta?.total || 0);
      }
    } catch (error) {
      toast({ title: 'Sync Error', description: 'Failed to refresh ledger.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, toast]);

  useEffect(() => { 
    fetchWalletData(); 
  }, [fetchWalletData]);

  const handleUploadProof = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('screenshot', selectedFile);
    formData.append('amount', rechargeAmount);

    try {
      const res = await api.post('/billing/upload-proof', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        toast({ title: "Success", description: "Verification request sent!" });
        setRechargeAmount('');
        setSelectedFile(null);
        handlePageChange(1);
        fetchWalletData();
      }
    } catch (error) {
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) endPage = Math.min(totalPages, 5);
    if (currentPage >= totalPages - 2) startPage = Math.max(1, totalPages - 4);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading && transactions.length === 0) return (
    <div className="h-[80vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      <p className="font-bold text-slate-500 tracking-tight">Accessing Ledger...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500">
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b pb-8">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-indigo-600 rounded-3xl shadow-xl shadow-indigo-100">
            <WalletIcon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tighter">My Wallet</h1>
            <p className="text-slate-500 font-medium">Manage liquidity and audit message costs.</p>
          </div>
        </div>

        {!isAdmin && (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-2xl h-14 px-8 font-black text-lg">
                <IndianRupee className="w-5 h-5 mr-2" /> Recharge Now
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[450px] rounded-[2.5rem] p-8">
              <DialogHeader>
                <DialogTitle className="text-3xl font-black">Add Credits</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUploadProof} className="w-full space-y-5">
                 <img src={paymentQR} alt="QR" className="w-48 h-48 mx-auto rounded-2xl border mb-4" />
                 <Input 
                   type="number" 
                   placeholder="Amount (₹)" 
                   value={rechargeAmount}
                   onChange={(e) => setRechargeAmount(e.target.value)}
                   className="rounded-xl h-12"
                   required
                 />
                 <Input 
                   type="file" 
                   accept="image/*" 
                   onChange={(e) => setSelectedFile(e.target.files[0])}
                   className="rounded-xl h-12"
                   required
                 />
                 <Button type="submit" className="w-full bg-indigo-600 h-14 font-black" disabled={uploading}>
                   {uploading ? <Loader2 className="animate-spin" /> : "Submit Proof"}
                 </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isAdmin && (
        <Card className="bg-slate-900 text-white border-none shadow-2xl rounded-[3rem] overflow-hidden">
          <CardContent className="p-16 flex flex-col md:flex-row justify-between items-center relative z-10">
            <div className="space-y-4">
              <p className="text-indigo-400 font-black uppercase tracking-widest text-xs">Active Balance</p>
              <h2 className="text-7xl font-black tracking-tighter">
                <span className="text-2xl mr-2 text-slate-500 font-normal">₹</span>
                {Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </h2>
            </div>
            <div className="p-6 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-xl">
              <Zap className="w-6 h-6 text-amber-400 mb-2" />
              <p className="text-xs text-slate-300 font-medium max-w-[200px]">Credits are deducted per campaign activity.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-[2.5rem] shadow-xl border-none overflow-hidden bg-white">
        <CardHeader className="bg-slate-50 border-b px-8 py-6 flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-black text-slate-800 flex items-center gap-2">
            <History className="w-5 h-5" /> Transaction Ledger
          </CardTitle>
          <Badge variant="outline" className="font-bold bg-white">Total: {totalRecords} Records</Badge>
        </CardHeader>
        <CardContent className="p-0 relative min-h-[300px]">
          
          {loading && transactions.length > 0 && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          )}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-8 py-5">Status</TableHead>
                  <TableHead>Description</TableHead>
                  {isAdmin && <TableHead>User</TableHead>}
                  <TableHead className="text-right">Amount (₹)</TableHead>
                  <TableHead className="text-center pr-8">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx._id} className="hover:bg-slate-50">
                    <TableCell className="pl-8">
                      <Badge className={`capitalize border-none ${tx.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : tx.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                        {tx.status || 'completed'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="font-bold text-slate-800">{tx.description}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">{new Date(tx.created_at).toLocaleString()}</p>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-semibold text-slate-700">{tx.username}</span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell className={`text-right font-black text-lg ${tx.type === 'credit' ? 'text-emerald-600' : 'text-slate-800'}`}>
                      {tx.type === 'credit' ? '+' : '-'}₹{Number(tx.amount).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center pr-8">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate(`/receipt/${tx._id}`)}
                        className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-bold rounded-xl"
                      >
                        <ReceiptText className="w-4 h-4 mr-2" /> View Invoice
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {transactions.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 5 : 4} className="h-48 text-center text-slate-500 font-medium">
                      No transactions found matching the criteria.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {totalPages > 1 && (
            <div className="p-6 bg-slate-50 border-t flex flex-col sm:flex-row justify-between items-center gap-4 rounded-b-[2.5rem]">
              <span className="text-sm font-bold text-slate-500">
                Showing Page <span className="text-slate-900">{currentPage}</span> of <span className="text-slate-900">{totalPages}</span>
              </span>
              
              <div className="flex items-center gap-2">
                <Button 
                  disabled={currentPage === 1 || loading} 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  variant="outline" 
                  size="icon" 
                  className="rounded-xl w-10 h-10 border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                
                <div className="hidden sm:flex items-center gap-1">
                  {getPageNumbers().map(pageNum => (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      className={`rounded-xl w-10 h-10 font-black transition-all ${
                        currentPage === pageNum 
                          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200 hover:bg-indigo-700' 
                          : 'border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {pageNum}
                    </Button>
                  ))}
                </div>

                <Button 
                  disabled={currentPage === totalPages || loading} 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  variant="outline" 
                  size="icon" 
                  className="rounded-xl w-10 h-10 border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}